import React from 'react';
import styled from '../../styled';
import { COLORS } from '../../themes/color';
import { useInView, usePrefersReducedMotion } from '../motion';
import {
  AppMockupChrome,
  MetricCell,
  MockCard,
  ProductMockupBody,
  ProductMockupFrame,
  SkeletonBar,
  TrendIcon,
  TrendTone
} from './shared';

const TitleRow = styled('div')`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  flex-wrap: wrap;
  margin-bottom: 12px;

  .title {
    font-size: 15px;
    font-weight: 700;
    color: #0e1c2b;
  }
`;

const RangeChip = styled('span')`
  font-size: 10px;
  font-weight: 500;
  color: #64748b;
  background: #eef1f4;
  border-radius: 100px;
  padding: 4px 10px;
  white-space: nowrap;
`;

const ChartCard = styled(MockCard)`
  padding: 14px;
  min-width: 0;
`;

const GroupLabel = styled('div')<{ spaced?: boolean }>`
  font-size: 9px;
  color: #94a3b8;
  margin-bottom: 6px;
  margin-top: ${(p) => (p.spaced ? '12px' : '0')};
`;

const BarRow = styled('div')`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 7px;
  min-width: 0;
`;

const BarLabel = styled('span')`
  width: 72px;
  flex-shrink: 0;
  font-size: 9.5px;
  color: #64748b;
  text-align: right;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const BarTrack = styled('div')`
  flex: 1;
  height: 10px;
  background: #f6f7f9;
  border-radius: 100px;
  overflow: hidden;
  min-width: 0;
`;

const BarFill = styled('div')<{
  visible: boolean;
  delayMs: number;
  reduced: boolean;
  pct: number;
  color: string;
}>`
  height: 100%;
  border-radius: 100px;
  background: ${(p) => p.color};
  width: ${(p) => (p.visible || p.reduced ? `${p.pct}%` : '0%')};
  transition: width 0.7s cubic-bezier(0.22, 1, 0.36, 1) ${(p) => p.delayMs}ms;
`;

const XAxis = styled('div')`
  display: flex;
  justify-content: space-between;
  margin-top: 8px;
  padding-left: 80px;

  span {
    font-size: 8.5px;
    color: #b0b7c3;
  }
`;

const SegmentTable = styled(MockCard)`
  overflow: hidden;
  margin-top: 12px;
  min-width: 0;

  table {
    width: 100%;
    border-collapse: collapse;
    font-size: 11px;
  }

  th {
    text-align: left;
    font-size: 9.5px;
    font-weight: 500;
    color: #b0b7c3;
    padding: 10px 12px 6px;
    white-space: nowrap;

    &.num {
      text-align: right;
    }

    &.rpm {
      @media (max-width: 560px) {
        display: none;
      }
    }

    &.clicks {
      @media (max-width: 460px) {
        display: none;
      }
    }
  }

  td {
    padding: 9px 12px;
    color: #334155;
    white-space: nowrap;
    vertical-align: middle;

    &.num {
      text-align: right;
    }

    &.rpm {
      @media (max-width: 560px) {
        display: none;
      }
    }

    &.clicks {
      @media (max-width: 460px) {
        display: none;
      }
    }
  }
`;

const AnimatedRow = styled('tr')<{
  visible: boolean;
  delayMs: number;
  reduced: boolean;
}>`
  border-top: 1px solid #f4f4f2;
  opacity: ${(p) => (p.visible || p.reduced ? 1 : 0)};
  transform: ${(p) =>
    p.visible || p.reduced ? 'translateY(0)' : 'translateY(8px)'};
  transition: opacity 0.5s ease ${(p) => p.delayMs}ms,
    transform 0.5s cubic-bezier(0.22, 1, 0.36, 1) ${(p) => p.delayMs}ms;
`;

const SegmentDot = styled('span')<{ color: string }>`
  width: 9px;
  height: 9px;
  border-radius: 50%;
  background: ${(p) => p.color};
  flex-shrink: 0;
`;

const SegmentCell = styled('div')`
  display: flex;
  align-items: center;
  gap: 7px;
  font-weight: 600;
  color: #0e1c2b;
  font-size: 10.5px;
`;

type BarItem = {
  label: string;
  pct: number;
  color: string;
};

const CONTENT_TYPE_BARS: BarItem[] = [
  { label: 'Roundups', pct: 88, color: COLORS.GOLD.gold4 },
  { label: 'Reviews', pct: 62, color: COLORS.GOLD.gold6 }
];

const PRODUCT_CATEGORY_BARS: BarItem[] = [
  { label: 'Luggage', pct: 71, color: COLORS.GREEN.green4 },
  { label: 'Backpacks', pct: 45, color: COLORS.GREEN.green6 },
  { label: 'Accessories', pct: 28, color: COLORS.GREEN.green8 }
];

type SegmentRow = {
  name: string;
  color: string;
  trends: [TrendTone, TrendTone, TrendTone, TrendTone];
  widths: [number, number, number, number];
};

const SEGMENT_ROWS: SegmentRow[] = [
  {
    name: 'Roundups',
    color: COLORS.GOLD.gold4,
    trends: ['up', 'up', 'up', 'up'],
    widths: [32, 28, 26, 34]
  },
  {
    name: 'Reviews',
    color: COLORS.GOLD.gold6,
    trends: ['up', 'up', 'up', 'up'],
    widths: [30, 27, 29, 31]
  },
  {
    name: 'Guides',
    color: COLORS.GREEN.green4,
    trends: ['partial', 'partial', 'partial', 'partial'],
    widths: [28, 30, 24, 27]
  },
  {
    name: 'Deals',
    color: COLORS.GREEN.green6,
    trends: ['partial', 'partial', 'partial', 'up'],
    widths: [26, 25, 28, 33]
  },
  {
    name: 'News',
    color: COLORS.GREEN.green8,
    trends: ['flat', 'flat', 'flat', 'flat'],
    widths: [24, 26, 25, 27]
  }
];

const renderBars = (
  bars: BarItem[],
  inView: boolean,
  reduced: boolean,
  startDelay: number
) =>
  bars.map((bar, index) => (
    <BarRow key={bar.label}>
      <BarLabel>{bar.label}</BarLabel>
      <BarTrack>
        <BarFill
          visible={inView}
          reduced={reduced}
          delayMs={startDelay + index * 90}
          pct={bar.pct}
          color={bar.color}
        />
      </BarTrack>
    </BarRow>
  ));

export const SegmentationMockup = () => {
  const reduced = usePrefersReducedMotion();
  const { ref, inView } = useInView<HTMLDivElement>({ threshold: 0.25 });

  return (
    <ProductMockupFrame ref={ref} aria-hidden>
      <AppMockupChrome product="Optimize" surface="Segments" />
      <ProductMockupBody>
        <TitleRow>
          <span className="title">Content segments</span>
          <RangeChip>Earnings · Last 90 Days</RangeChip>
        </TitleRow>

        <ChartCard>
          <GroupLabel>Content type</GroupLabel>
          {renderBars(CONTENT_TYPE_BARS, inView, reduced, 100)}

          <GroupLabel spaced>Product category</GroupLabel>
          {renderBars(PRODUCT_CATEGORY_BARS, inView, reduced, 280)}

          <XAxis>
            <span>$0</span>
            <span>$20K</span>
            <span>$40K</span>
            <span>$60K</span>
          </XAxis>
        </ChartCard>

        <SegmentTable>
          <table>
            <thead>
              <tr>
                <th>Segment</th>
                <th className="num">Pageviews</th>
                <th className="num clicks">Clicks</th>
                <th className="num rpm">RPM</th>
                <th className="num">Earnings</th>
              </tr>
            </thead>
            <tbody>
              {SEGMENT_ROWS.map((row, index) => (
                <AnimatedRow
                  key={row.name}
                  visible={inView}
                  delayMs={250 + index * 90}
                  reduced={reduced}
                >
                  <td>
                    <SegmentCell>
                      <SegmentDot color={row.color} />
                      {row.name}
                    </SegmentCell>
                  </td>
                  {row.trends.map((tone, colIndex) => (
                    <td
                      key={colIndex}
                      className={`num${
                        colIndex === 1
                          ? ' clicks'
                          : colIndex === 2
                          ? ' rpm'
                          : ''
                      }`}
                    >
                      <MetricCell>
                        <SkeletonBar w={row.widths[colIndex]} h={7} />
                        <TrendIcon tone={tone} />
                      </MetricCell>
                    </td>
                  ))}
                </AnimatedRow>
              ))}
            </tbody>
          </table>
        </SegmentTable>
      </ProductMockupBody>
    </ProductMockupFrame>
  );
};
